<template>
  <BaseCardBody>
    <h3 class="text-base font-medium mb-4">
      {{ $t('importContactsModal.processSection.mapping.title') }}
    </h3>
    <div class="flex flex-col">
      <div class="overflow-x-auto border">
        <div class="inline-block min-w-full align-middle">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5">
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr class="divide-x divide-gray-200">
                  <th
                    v-for="(heading, headingIdx) in headings"
                    :key="`head_${headingIdx}_${heading}`"
                    scope="col"
                    class="py-3.5 text-left text-sm font-semibold text-gray-900"
                    :class="[
                      headingIdx === 0 ? 'pl-4 pr-3 sm:pl-6' : '',
                      headingIdx > 0 && headingIdx < headings.length - 1 ? 'px-3' : '',
                      headingIdx === headings.length - 1 ? 'pl-3 pr-4 sm:pr-6' : '',
                    ]"
                  >
                    {{ heading }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr class="divide-x divide-gray-200">
                  <td
                    v-for="(heading, headingIdx) in headings"
                    :key="`input_${headingIdx}_${heading}`"
                    class="whitespace-nowrap py-4 text-sm text-gray-900"
                    :class="[
                      headingIdx === 0 ? 'pl-4 pr-3 sm:pl-6' : '',
                      headingIdx > 0 && headingIdx < headings.length - 1 ? 'px-3' : '',
                      headingIdx === headings.length - 1 ? 'pl-3 pr-4 sm:pr-6' : '',
                    ]"
                  >
                    <Select
                      class="form-select w-full"
                      @change="updateFieldMap(heading, $event.target.value)"
                    >
                      <Option value="" class="capitalize">
                        {{ $t('importContactsModal.processSection.mapping.select') }}
                      </Option>
                      <Option
                        v-for="option in fieldOptions"
                        :key="option"
                        :value="option"
                        :disabled="usedOptions.indexOf(option) >= 0"
                      >
                        <!-- {{ option }} -->
                        {{ $t(`importContactsModal.processSection.mapping.${option}`) }}
                      </Option>
                    </Select>
                  </td>
                </tr>
                <tr v-for="(row, rowIdx) in rows" :key="rowIdx" class="divide-x divide-gray-200">
                  <td
                    v-for="(col, colIdx) in row"
                    :key="`col_${colIdx}_${col}`"
                    class="whitespace-nowrap py-4 text-sm text-gray-900"
                    :class="[
                      colIdx === 0 ? 'pl-4 pr-3 sm:pl-6' : '',
                      colIdx > 0 && colIdx < row.length - 1 ? 'px-3' : '',
                      colIdx === row.length - 1 ? 'pl-3 pr-4 sm:pr-6' : '',
                    ]"
                  >
                    {{ col }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-8">
      <h3 class="text-base font-medium mb-4">
        {{ $t('importContactsModal.processSection.contactListSelection.title') }}
      </h3>
      <div class="space-y-4">
        <BaseFormGroup>
          <BaseFormCheckbox v-model="useExistingContactList">
            {{ $t('importContactsModal.processSection.contactListSelection.selectExistingList') }}
          </BaseFormCheckbox>
        </BaseFormGroup>
        <BaseFormGroup v-if="!useExistingContactList">
          <BaseFormLabel>{{
            $t('importContactsModal.processSection.contactListSelection.listName')
          }}</BaseFormLabel>
          <BaseFormInput v-model="contactList.name" class="max-w-lg" />
        </BaseFormGroup>
        <BaseFormGroup v-else>
          <BaseFormLabel>{{
            $t('importContactsModal.processSection.contactListSelection.list')
          }}</BaseFormLabel>
          <div class="max-w-lg">
            <ContactListSelectField v-model="contactList.id" :account-id="accountId" />
          </div>
        </BaseFormGroup>
        <BaseFormGroup>
          <div class="flex gap-3">
            <div class="flex-none">
              <BaseFormCheckbox v-model="emailMarketingPermission" />
            </div>
            <div class="flex-1">
              <p class="font-medium text-sm text-gray-900">
                {{ $t('importContactsModal.processSection.emailMarketingPermission.label') }}
              </p>
              <p class="text-sm text-gray-600">
                {{ $t('importContactsModal.processSection.emailMarketingPermission.description') }}
              </p>
            </div>
          </div>
        </BaseFormGroup>
      </div>
    </div>
    <div class="mt-8">
      <BaseVeeForm :action="beginImport">
        <template #default="formProps">
          <ValidationProvider name="first name" slim vid="first_name" :rules="{ required: false }">
            <template>
              <BaseFormGroup>
                <input type="hidden" :value="contactFieldToHeadingMap.first_name || ''" />
              </BaseFormGroup>
            </template>
          </ValidationProvider>
          <ValidationProvider name="last name" slim vid="last_name" :rules="{ required: false }">
            <template>
              <BaseFormGroup>
                <input type="hidden" :value="contactFieldToHeadingMap.last_name || ''" />
              </BaseFormGroup>
            </template>
          </ValidationProvider>
          <ValidationProvider name="email" slim vid="email" :rules="{ required: false }">
            <template>
              <BaseFormGroup>
                <input type="hidden" :value="contactFieldToHeadingMap.email || ''" />
              </BaseFormGroup>
            </template>
          </ValidationProvider>
          <ValidationProvider name="phone" slim vid="phone" :rules="{ required: false }">
            <template>
              <BaseFormGroup>
                <input type="hidden" :value="contactFieldToHeadingMap.phone || ''" />
              </BaseFormGroup>
            </template>
          </ValidationProvider>
          <ValidationProvider
            v-if="useExistingContactList"
            name="contact list"
            slim
            vid="contact_list.id"
            :rules="{ required: true }"
          >
            <template>
              <BaseFormGroup>
                <input type="hidden" :value="contactList.id" />
              </BaseFormGroup>
            </template>
          </ValidationProvider>
          <ValidationProvider
            v-else
            name="contact list name"
            slim
            vid="contact_list.name"
            :rules="{ required: true }"
          >
            <template>
              <BaseFormGroup>
                <input type="hidden" :value="contactList.name" />
              </BaseFormGroup>
            </template>
          </ValidationProvider>
          <ValidationProvider
            name="Email marketing permission"
            slim
            vid="email_marketing_permission"
          >
            <template>
              <BaseFormGroup>
                <input type="hidden" :value="emailMarketingPermission" />
              </BaseFormGroup>
            </template>
          </ValidationProvider>
          <div
            v-if="hasErrors(formProps.errors)"
            class="bg-red-50 border border-red-600 rounded p-4 mb-8"
          >
            <BaseFormInlineMessage
              v-if="'first_name' in formProps.errors && formProps.errors.first_name.length"
              danger
            >
              {{ formProps.errors.first_name[0] }}
            </BaseFormInlineMessage>
            <BaseFormInlineMessage
              v-if="'last_name' in formProps.errors && formProps.errors.last_name.length"
              danger
            >
              {{ formProps.errors.last_name[0] }}
            </BaseFormInlineMessage>
            <BaseFormInlineMessage
              v-if="'email' in formProps.errors && formProps.errors.email.length"
              danger
            >
              {{ formProps.errors.email[0] }}
            </BaseFormInlineMessage>
            <BaseFormInlineMessage
              v-if="'phone' in formProps.errors && formProps.errors.phone.length"
              danger
            >
              {{ formProps.errors.phone[0] }}
            </BaseFormInlineMessage>
            <BaseFormInlineMessage
              v-if="
                'contact_list.id' in formProps.errors && formProps.errors['contact_list.id'].length
              "
              danger
            >
              {{ formProps.errors['contact_list.id'][0] }}
            </BaseFormInlineMessage>
            <BaseFormInlineMessage
              v-if="
                'contact_list.name' in formProps.errors &&
                formProps.errors['contact_list.name'].length
              "
              danger
            >
              {{ formProps.errors['contact_list.name'][0] }}
            </BaseFormInlineMessage>
            <BaseFormInlineMessage
              v-if="
                'email_marketing_permission' in formProps.errors &&
                formProps.errors['email_marketing_permission'].length
              "
              danger
            >
              {{ formProps.errors['email_marketing_permission'][0] }}
            </BaseFormInlineMessage>
          </div>
          <BaseButton primary :disabled="formProps.ivalid">
            {{ $t('importContactsModal.processSection.contactListSelection.submit') }}
          </BaseButton>
        </template>
      </BaseVeeForm>
    </div>
  </BaseCardBody>
</template>

<script>
import { importContactsClient } from '@/api/clients';
import { invert, omitBy } from 'lodash-es';
import ContactListSelectField from './ContactListSelectField.vue';

export default {
  components: {
    ContactListSelectField,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    uploadId: {
      type: String,
      required: true,
    },
    hasHeadings: Boolean,
    headings: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    totalRows: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      fieldMap: {},
      contactList: {
        name: '',
        id: null,
      },
      emailMarketingPermission: false,
      modifyUnsubscribed: false,
      modifyBlacklisted: false,
      useExistingContactList: false,
    };
  },
  computed: {
    fieldOptions() {
      return ['first_name', 'last_name', 'email', 'phone', 'tags'];
    },
    usedOptions() {
      return Object.values(this.fieldMap || {}).filter(Boolean);
    },
    contactFieldToHeadingMap() {
      return {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        tags: '',
        ...invert(omitBy(this.fieldMap, (value) => !value)),
      };
    },
  },
  watch: {
    useExistingContactList(value) {
      if (value) {
        this.contactList.name = '';
      } else {
        this.contactList.id = null;
      }
    },
  },
  methods: {
    hasErrors(err) {
      return Object.values(err).some((item) => item.length > 0);
    },
    updateFieldMap(heading, field) {
      this.fieldMap = { ...this.fieldMap, [heading]: field };
    },
    beginImport() {
      return importContactsClient
        .process(this.accountId, {
          upload_id: this.uploadId,
          has_headings: this.hasHeadings,
          email_marketing_permission: this.emailMarketingPermission,
          field_map: omitBy(this.contactFieldToHeadingMap, (value) => !value),
          contact_list: this.useExistingContactList
            ? { id: this.contactList.id }
            : { name: this.contactList.name },
        })
        .then(() => {
          this.$emit('started');
          return Promise.resolve();
        });
    },
  },
};
</script>
